import { useEffect, useMemo, useState, type ReactNode } from 'react'
import ReactMarkdown from 'react-markdown'
import type { Components } from 'react-markdown'
import remarkGfm from 'remark-gfm'
import rehypeRaw from 'rehype-raw'
import rehypeSanitize, { defaultSchema } from 'rehype-sanitize'
import rehypeHighlight from 'rehype-highlight'
import 'highlight.js/styles/github.css'
import { looksLikeMermaid, MermaidBlock } from '../learning/MermaidDiagram'
import { SkillApi } from '../../services/skillApi'

/** Flatten highlighted / nested code children back to plain Mermaid source. */
function flattenText(node: ReactNode): string {
  if (node == null || typeof node === 'boolean') return ''
  if (typeof node === 'string' || typeof node === 'number') return String(node)
  if (Array.isArray(node)) return node.map(flattenText).join('')
  if (typeof node === 'object' && 'props' in node) {
    return flattenText((node as { props?: { children?: ReactNode } }).props?.children)
  }
  return ''
}

/**
 * Markdown uploaded by admins may contain inline HTML (details/summary, colored
 * spans...). Keep sanitize on, but widen the default schema so that formatting
 * survives.
 */
const sanitizeSchema = {
  ...defaultSchema,
  tagNames: [
    ...(defaultSchema.tagNames || []),
    'details',
    'summary',
    'mark',
    'kbd',
    'figure',
    'figcaption',
  ],
  attributes: {
    ...defaultSchema.attributes,
    '*': [...(defaultSchema.attributes?.['*'] || []), 'className', 'style', 'align'],
  },
}

const PROSE_CLASS = [
  // Light "paper" look: dark code slabs are hard to read inside the dark app shell.
  'prose prose-base max-w-none rounded-eh-sm bg-eh-cream px-6 py-5 leading-7 text-eh-charcoal',
  'prose-headings:text-eh-forest-ink prose-headings:font-semibold',
  'prose-h1:text-2xl prose-h2:text-xl prose-h3:text-lg',
  'prose-p:text-eh-charcoal',
  'prose-li:text-eh-charcoal prose-ul:text-eh-charcoal prose-ol:text-eh-charcoal',
  'prose-strong:text-eh-forest-ink',
  'prose-em:text-eh-charcoal',
  'prose-a:text-eh-forest-ink',
  'prose-code:text-eh-forest-ink prose-code:bg-eh-keylime prose-code:px-1 prose-code:rounded',
  'prose-code:before:content-none prose-code:after:content-none',
  'prose-pre:bg-eh-keylime/45 prose-pre:text-eh-charcoal prose-pre:border prose-pre:border-eh-border',
  'prose-blockquote:text-eh-muted prose-blockquote:border-eh-sage prose-blockquote:bg-eh-keylime/45',
  'prose-hr:border-eh-border',
  'prose-table:text-sm prose-th:text-eh-forest-ink prose-td:text-eh-charcoal',
  'prose-th:border prose-th:border-eh-sage prose-th:bg-eh-keylime prose-th:px-3 prose-th:py-2',
  'prose-td:border prose-td:border-eh-border prose-td:px-3 prose-td:py-2',
  // code inside <pre> keeps the slab background, not the inline-code chip
  '[&_pre_code]:bg-transparent [&_pre_code]:p-0 [&_pre_code]:text-eh-charcoal',
  // long lines wrap instead of forcing the panel to scroll sideways
  '[&_pre]:whitespace-pre-wrap [&_pre]:break-words',
].join(' ')

/** A reference that must be resolved against the folder of the current file. */
function isRelativeRef(src?: string): src is string {
  if (!src) return false
  const value = src.trim()
  if (!value || value.startsWith('#')) return false
  return !/^([a-z][a-z0-9+.-]*:|\/\/|\/)/i.test(value)
}

async function fetchAsObjectUrl(nodeId: number, courseId?: number): Promise<string> {
  const headers: HeadersInit = {}
  const token = localStorage.getItem('accessToken')
  if (token) headers.Authorization = `Bearer ${token}`
  const res = await fetch(SkillApi.fileStreamUrl(nodeId, { courseId }), { headers })
  if (!res.ok) throw new Error(`HTTP ${res.status}`)
  return URL.createObjectURL(await res.blob())
}

/** Image written as ![alt](images/a.png) — resolve to a sibling node, then stream it. */
const MarkdownImage = ({
  src,
  alt,
  title,
  baseNodeId,
  courseId,
}: {
  src?: string
  alt?: string
  title?: string
  baseNodeId?: number
  courseId?: number
}) => {
  const [url, setUrl] = useState<string | null>(null)
  const [failed, setFailed] = useState(false)
  const needsResolve = isRelativeRef(src) && !!baseNodeId

  useEffect(() => {
    if (!needsResolve) return
    let objectUrl: string | null = null
    let cancelled = false

    const load = async () => {
      try {
        const target = await SkillApi.resolveRelativeFile(baseNodeId!, src!, { courseId })
        objectUrl = await fetchAsObjectUrl(target.id, courseId)
        if (cancelled) return
        setUrl(objectUrl)
      } catch {
        if (!cancelled) setFailed(true)
      }
    }
    load()

    return () => {
      cancelled = true
      if (objectUrl) URL.revokeObjectURL(objectUrl)
    }
  }, [needsResolve, baseNodeId, src, courseId])

  if (!needsResolve) {
    return <img src={src} alt={alt || ''} title={title} className="max-w-full rounded" />
  }
  if (failed) {
    return (
      <span className="inline-block rounded bg-amber-50 px-2 py-1 text-xs text-amber-800">
        Không tìm thấy ảnh: {src}
      </span>
    )
  }
  if (!url) {
    return <span className="text-xs text-eh-muted">Đang tải ảnh {alt || src}...</span>
  }
  return <img src={url} alt={alt || ''} title={title} className="max-w-full rounded" />
}

/** Link to another file in the same pack — open the streamed blob in a new tab. */
const MarkdownLink = ({
  href,
  children,
  baseNodeId,
  courseId,
}: {
  href?: string
  children?: React.ReactNode
  baseNodeId?: number
  courseId?: number
}) => {
  const isInternalFile = isRelativeRef(href) && !!baseNodeId

  if (!isInternalFile) {
    const external = /^https?:/i.test(href || '')
    return (
      <a
        href={href}
        target={external ? '_blank' : undefined}
        rel={external ? 'noopener noreferrer' : undefined}
      >
        {children}
      </a>
    )
  }

  return (
    <a
      href={href}
      className="cursor-pointer underline decoration-dotted"
      onClick={async (e) => {
        e.preventDefault()
        try {
          const target = await SkillApi.resolveRelativeFile(baseNodeId!, href!, { courseId })
          const url = await fetchAsObjectUrl(target.id, courseId)
          window.open(url, '_blank', 'noopener')
        } catch {
          // file không tồn tại trong pack — giữ nguyên hành vi không điều hướng
        }
      }}
    >
      {children}
    </a>
  )
}

interface SkillMarkdownProps {
  body: string
  /** Node id of the .md file, used to resolve relative images/links. */
  baseNodeId?: number
  courseId?: number
}

const SkillMarkdown = ({ body, baseNodeId, courseId }: SkillMarkdownProps) => {
  const components = useMemo<Components>(
    () => ({
      img: ({ node: _node, ...props }: any) => (
        <MarkdownImage {...props} baseNodeId={baseNodeId} courseId={courseId} />
      ),
      a: ({ node: _node, ...props }: any) => (
        <MarkdownLink {...props} baseNodeId={baseNodeId} courseId={courseId} />
      ),
      table: ({ node: _node, ...props }: any) => (
        <div className="overflow-x-auto">
          <table {...props} />
        </div>
      ),
      // Same pattern as AskAiMarkdown: fenced ```mermaid → diagram, not a code slab.
      code({ className, children, ...props }) {
        const text = flattenText(children).replace(/\n$/, '')
        const lang = /language-(\w+)/.exec(className || '')?.[1]?.toLowerCase()
        const isBlock = Boolean(className) || text.includes('\n')
        if (!isBlock) {
          return (
            <code className={className} {...props}>
              {children}
            </code>
          )
        }
        if (looksLikeMermaid(text, lang)) {
          return <MermaidBlock code={text} idPrefix="skill-mmd" />
        }
        return (
          <pre>
            <code className={className} {...props}>
              {children}
            </code>
          </pre>
        )
      },
      pre({ children }) {
        return <>{children}</>
      },
    }),
    [baseNodeId, courseId]
  )

  return (
    <article className={PROSE_CLASS}>
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        rehypePlugins={[
          rehypeRaw,
          [rehypeSanitize, sanitizeSchema],
          [rehypeHighlight, { ignoreMissing: true, detect: false }],
        ]}
        components={components}
      >
        {body}
      </ReactMarkdown>
    </article>
  )
}

export default SkillMarkdown
